import React, { useEffect, useMemo, useState } from "react";
import { Box, LinearProgress } from '@mui/joy';
import { useDataProvider } from "src/mtska-frontend-data-provider";

/**
 * Formatta una chiave "YYYY-MM" in etichetta leggibile "mmm YYYY"
 */
function formatMonthLabel(key) {
    const [year, month] = key.split("-");
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleDateString("it-IT", { month: "short", year: "numeric" });
}

/**
 * Calcola l'intensità heatmap (0–1) rispetto al massimo globale per colonna.
 * Ritorna undefined se value è 0.
 */
function useHeatmap(data) {
    return useMemo(() => {
        if (!data || !data.columns) return {};
        const maxByColumn = {};
        data.columns.forEach((col) => {
            let max = 0;
            data.rows.forEach((row) => {
                row.areas.forEach((area) => {
                    const v = area.values[col] ?? 0;
                    if (v > max) max = v;
                });
            });
            maxByColumn[col] = max;
        });
        return maxByColumn;
    }, [data]);
}

const PivotTable = ({
    dataProviderName,
    filters,
    compareFilters = null,
    rowGroupLabel = "Partner",
    rowLeafLabel = "Area",
    valueFormatter = (v) => (v === 0 ? "–" : v.toLocaleString("it-IT")),
}) => {

    const [drawable, setDrawable] = useState();
    const [data, setData] = useState({});
    const [knownFilters, setKnownFilters] = useState();
    const [areasCollapsed, setAreasCollapsed] = useState(false);

    const { items, loadAll, reloadCounter } = useDataProvider(dataProviderName);
    useEffect(() => {
        const shouldReload = (JSON.stringify(filters) != knownFilters) || (typeof filters == typeof undefined && items.length == 0);

        if (shouldReload) {
            setKnownFilters(JSON.stringify(filters));
            loadAll(filters, null, null, null, compareFilters);
        }
    }, [reloadCounter, filters]);

    useEffect(() => {
        if (Array.isArray(items)) return;

        console.info("PIVOT ciao", items);
        setData(items);
        setDrawable(true);
    }, [items]);

    const maxByColumn = useHeatmap(data);

    const { columns, rows, grandTotal } = data;

    return (!drawable ? <LinearProgress /> :
        <div className="mtska-pivot-wrapper">
            <div className="mtska-pivot-scroll">
                <table className="mtska-pivot-table">
                    {/* HEAD */}
                    <thead className="mtska-pivot-thead">
                        <tr className="mtska-pivot-thead__row">
                            <th className="mtska-pivot-thead__cell mtska-pivot-thead__cell--group">
                                {rowGroupLabel}
                                <button
                                    className={`mtska-pivot-toggle ${areasCollapsed ? "mtska-pivot-toggle--collapsed" : ""}`}
                                    onClick={() => setAreasCollapsed((v) => !v)}
                                    title={areasCollapsed ? `Espandi ${rowLeafLabel}` : `Comprimi ${rowLeafLabel}`}
                                >
                                    <span className="mtska-pivot-toggle__icon">▾</span>
                                    <span className="mtska-pivot-toggle__label">{rowLeafLabel}</span>
                                </button>
                            </th>
                            <th className="mtska-pivot-thead__cell mtska-pivot-thead__cell--leaf">
                                {rowLeafLabel}
                            </th>
                            {columns.map((col) => (
                                <th key={col} className="mtska-pivot-thead__cell mtska-pivot-thead__cell--month">
                                    {formatMonthLabel(col)}
                                </th>
                            ))}
                            <th className="mtska-pivot-thead__cell mtska-pivot-thead__cell--total">
                                Totale
                            </th>
                        </tr>
                    </thead>

                    {/* BODY */}
                    <tbody className="mtska-pivot-tbody">
                        {rows.map((row) => (
                            <PartnerRows
                                key={row.partner}
                                row={row}
                                columns={columns}
                                maxByColumn={maxByColumn}
                                valueFormatter={valueFormatter}
                                areasCollapsed={areasCollapsed}
                            />
                        ))}
                    </tbody>

                    {/* FOOT — grand total */}
                    <tfoot className="mtska-pivot-tfoot">
                        <tr className="mtska-pivot-tfoot__row">
                            <td
                                className="mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--label"
                                colSpan={2}
                            >
                                Totale complessivo
                            </td>
                            {columns.map((col) => (
                                <td key={col} className="mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--value">
                                    {valueFormatter(grandTotal.values[col] ?? 0)}
                                </td>
                            ))}
                            <td className="mtska-pivot-tfoot__cell mtska-pivot-tfoot__cell--total">
                                {valueFormatter(grandTotal.total)}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sub-componenti
// ---------------------------------------------------------------------------

function PartnerRows({ row, columns, maxByColumn, valueFormatter, areasCollapsed }) {
    return (
        <>
            {/* Righe area — nascoste se collapsed */}
            {!areasCollapsed && row.areas.map((area, idx) => (
                <tr
                    key={area.area}
                    className={`mtska-pivot-tbody__row mtska-pivot-tbody__row--area ${idx === 0 ? "mtska-pivot-tbody__row--first" : ""
                        }`}
                >
                    {/* Cella partner: solo sulla prima riga del gruppo */}
                    {idx === 0 && (
                        <td
                            className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--group"
                            rowSpan={row.areas.length + 1}
                        >
                            {row.partner}
                        </td>
                    )}

                    <td className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--leaf">
                        {area.area}
                    </td>

                    {columns.map((col) => {
                        const value = area.values[col] ?? 0;
                        const intensity =
                            maxByColumn[col] > 0 ? value / maxByColumn[col] : 0;
                        return (
                            <td
                                key={col}
                                className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--value"
                                style={
                                    value > 0
                                        ? { "--heat-intensity": intensity.toFixed(3) }
                                        : undefined
                                }
                                data-heat={value > 0 ? "true" : undefined}
                            >
                                {valueFormatter(value)}
                            </td>
                        );
                    })}

                    <td className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--rowtotal">
                        {valueFormatter(area.area_total)}
                    </td>
                </tr>
            ))}

            {/* Riga totale partner */}
            <tr className={`mtska-pivot-tbody__row mtska-pivot-tbody__row--subtotal ${areasCollapsed ? "mtska-pivot-tbody__row--first" : ""}`}>
                {areasCollapsed && (
                    <td className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--group">
                        {row.partner}
                    </td>
                )}
                <td className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-label">
                    Totale
                </td>
                {columns.map((col) => {
                    const value = row.areas.reduce((acc, a) => acc + (a.values[col] ?? 0), 0);
                    return (
                        <td key={col} className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-value">
                            {valueFormatter(value)}
                        </td>
                    );
                })}
                <td className="mtska-pivot-tbody__cell mtska-pivot-tbody__cell--subtotal-total">
                    {valueFormatter(row.partner_total)}
                </td>
            </tr>
        </>
    );
}

export default PivotTable;